import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ShieldAlert, LogOut, Mail } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/core/supabase';
import { useSession } from '@/core/session';
import { useT } from '@/core/i18n';
import { readMfa, verifyCode, factorToChallenge } from '@/core/mfa';
import { sendEmailCode, verifyEmailCode } from '@/core/mfaEmail';
import { errorMessage } from '@/core/errorMessage';
import { BrandMark } from '@/components/brand';
import { Button } from '@/components/ui';
import { FullScreenLoader, Spinner } from '@/components/Loader';

/* =============================================================================
 * The door, for accounts that have a second lock on it
 * -----------------------------------------------------------------------------
 * Not on the login form, deliberately. A password is only one of the ways into
 * this app — there is a fingerprint unlock, a session restored from the last
 * time the phone was opened, and a deep link that lands somebody straight on a
 * payslip. Putting the check on the form would cover one of the four.
 *
 * So it sits above everything a signed-in person can see, and every route
 * arrives through it.
 *
 * =============================================================================
 * TWO DIFFERENT STOPS THAT LOOK THE SAME AND ARE NOT
 * =============================================================================
 *   owes a code    the account has a factor and this session has not used it.
 *                  Auth knows; the session is aal1 and cannot be raised
 *                  without six digits.
 *
 *   owes a factor  the company has started insisting and this person has not
 *                  set one up. Auth is perfectly happy; the database is not,
 *                  and app.current_permissions() is returning nothing.
 *
 * The second is the one worth being careful about, because without a screen
 * for it the app simply looks empty — every list, every figure, every screen,
 * blank, with nothing anywhere saying why. That is indistinguishable from a
 * company whose data has vanished, which is a support call at midnight.
 * ========================================================================== */

export function RequireSecondFactor({ children }: { children: ReactNode }) {
  const { session, profile } = useSession();

  const state = useQuery({
    queryKey: ['second-factor', session?.user?.id],
    queryFn: async () => {
      const [mfa, { data: security }] = await Promise.all([
        readMfa(),
        supabase.rpc('my_security'),
      ]);

      const rules = (security ?? {}) as {
        satisfied?: boolean;
        companyRequires?: string;
        appliesToMe?: boolean;
        emailAllowed?: boolean;
        emailHint?: string | null;
      };

      return {
        mustVerify: mfa.mustVerify,
        hasFactor: mfa.factors.some((f) => f.verified),
        satisfied: rules.satisfied !== false,
        appliesToMe: rules.appliesToMe === true,
        /* The other route. Needs no enrolment — every account has an address
           since 0128 — which is the whole reason it exists. */
        emailAllowed: rules.emailAllowed === true,
        emailHint: rules.emailHint ?? null,
      };
    },
    enabled: Boolean(session && profile),
    /* Re-asked on every focus. Somebody enrolling on their phone while this tab
       is open should not have to reload to get their app back. */
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });

  if (!session || !profile) return <>{children}</>;
  /* Full screen, not a bare loader. Unwrapped, BrandLoader is a centred
     column in the normal flow — so it drew the mark at the TOP of the page,
     which is what somebody saw arriving as the boot overlay faded out. */
  if (state.isLoading) return <FullScreenLoader />;

  // A network fault must not lock somebody out of their own workspace. The
  // database is still enforcing this either way; what is at stake here is only
  // whether the right screen is shown.
  if (state.isError || !state.data) return <>{children}</>;

  /* An authenticator is enrolled and this session has not used it. */
  if (state.data.mustVerify) {
    return (
      <Challenge
        hasApp
        emailAllowed={state.data.emailAllowed}
        emailHint={state.data.emailHint}
        onDone={() => void state.refetch()}
      />
    );
  }

  if (!state.data.satisfied) {
    /* No authenticator, but the company allows a code to the mailbox — so this
       person is not stuck, they simply have not been sent one yet. Offering the
       enrolment screen here would be telling somebody to install an app they
       may not be able to install, when the door beside them is open. */
    if (state.data.emailAllowed) {
      return (
        <Challenge
          hasApp={false}
          emailAllowed
          emailHint={state.data.emailHint}
          onDone={() => void state.refetch()}
        />
      );
    }

    if (!state.data.hasFactor) return <MustEnrol />;
  }

  return <>{children}</>;
}

/* -----------------------------------------------------------------------------
 * Six digits, from one of two places
 * -----------------------------------------------------------------------------
 * The app code and the emailed code are the same six digits to the person
 * typing them, and two different mechanisms underneath: one is verified by
 * GoTrue and raises `aal2`, the other is verified by an Edge Function and marks
 * the session. The screen hides that difference on purpose — what a supervisor
 * needs to decide is where to read the number from, not which system checks it.
 *
 * The authenticator comes first when there is one. It is the stronger factor,
 * it needs no network round trip and no waiting for mail, and somebody who has
 * gone to the trouble of enrolling one should not have to step past it.
 * -------------------------------------------------------------------------- */
function Challenge({
  hasApp, emailAllowed, emailHint, onDone,
}: {
  hasApp: boolean;
  emailAllowed: boolean;
  emailHint: string | null;
  onDone: () => void;
}) {
  const t = useT();
  const queryClient = useQueryClient();

  /* Which route is on screen. Starts on the app when there is one, because that
     is the one that needs no waiting. */
  const [route, setRoute] = useState<'app' | 'email'>(hasApp ? 'app' : 'email');

  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentAt, setSentAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => { if (hasApp) void factorToChallenge().then(setFactorId); }, [hasApp]);

  async function send() {
    setError(null);
    setNote(null);
    setSending(true);
    try {
      const { expiresInMinutes } = await sendEmailCode();
      setSentAt(Date.now());
      setNote(t('sf.codeSent', { minutes: expiresInMinutes }));
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSending(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      if (route === 'app') {
        if (!factorId) return;
        await verifyCode(factorId, code);
      } else {
        await verifyEmailCode(code);
      }

      /* Everything already fetched came back through a session that had not
         proved anything, which is to say it came back empty. */
      await queryClient.invalidateQueries();
      onDone();
    } catch (caught) {
      setError(errorMessage(caught));
      setCode('');
      setBusy(false);
    }
  }

  const ready = route === 'app' ? Boolean(factorId) : sentAt !== null;

  return (
    <Shell
      icon={<ShieldCheck size={22} />}
      title={t('sf.title')}
      body={route === 'app' ? t('sf.blurb') : t('sf.blurbEmail')}
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        {/* The email route has a step before the digits: there is nothing to
            type until a code has been sent. Offered as a button rather than
            sent automatically — a code sent by a screen somebody merely passed
            through is a code in an inbox nobody asked for, and this screen is
            reached by every deep link into the app. */}
        {route === 'email' && sentAt === null && (
          <div className="flex flex-col gap-2">
            <p className="text-[12.5px] text-muted leading-snug text-center">
              {emailHint
                ? t('sf.willSendTo', { where: emailHint })
                : t('sf.willSend')}
            </p>
            <Button
              type="button"
              onClick={() => void send()}
              disabled={sending}
              icon={sending ? <Spinner size={15} /> : <Mail size={15} />}
              className="w-full"
            >
              {sending ? t('sf.sending') : t('sf.sendCode')}
            </Button>
          </div>
        )}

        {ready && (
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            aria-label={t('sf.codeLabel')}
            placeholder="000000"
            className="w-full h-14 px-3 bg-surface-2 border border-line rounded-card
                       font-mono text-[24px] tracking-[.35em] text-center outline-none
                       focus:border-brand"
          />
        )}

        {note && (
          <p className="text-[12.5px] text-ok bg-ok-subtle rounded-card px-3 py-2.5 leading-snug">
            {note}
          </p>
        )}

        {error && (
          <p role="alert" className="text-[12.5px] text-danger bg-danger-subtle rounded-card px-3 py-2.5 leading-snug">
            {error}
          </p>
        )}

        {ready && (
          <Button
            type="submit"
            disabled={busy || code.length < 6}
            icon={busy ? <Spinner size={15} /> : <ShieldCheck size={15} />}
            className="w-full"
          >
            {busy ? t('sf.checking') : t('sf.confirm')}
          </Button>
        )}

        {/* The other door. Only offered when there is one — a company that has
            switched the email route off should not be shown a link to it, and
            somebody with no authenticator should not be offered one. */}
        {hasApp && emailAllowed && (
          <button
            type="button"
            onClick={() => {
              setRoute((r) => (r === 'app' ? 'email' : 'app'));
              setCode('');
              setError(null);
              setNote(null);
            }}
            className="text-[12px] text-muted hover:text-text press text-center"
          >
            {route === 'app' ? t('sf.useEmailInstead') : t('sf.useAppInstead')}
          </button>
        )}

        {route === 'email' && sentAt !== null && (
          <button
            type="button"
            onClick={() => void send()}
            disabled={sending}
            className="text-[12px] text-muted hover:text-text press text-center"
          >
            {t('sf.sendAnother')}
          </button>
        )}

        <p className="text-[11.5px] text-subtle leading-snug text-center">
          {emailAllowed ? t('sf.lostPhoneEmail') : t('sf.lostPhone')}
        </p>
      </form>
    </Shell>
  );
}

/* -----------------------------------------------------------------------------
 * A company that has started insisting
 * -------------------------------------------------------------------------- */
function MustEnrol() {
  const t = useT();
  const navigate = useNavigate();

  return (
    <Shell
      icon={<ShieldAlert size={22} />}
      tone="warn"
      title={t('sf.enrolTitle')}
      body={t('sf.enrolBlurb')}
    >
      <div className="flex flex-col gap-2">
        <Button
          className="w-full"
          icon={<ShieldCheck size={15} />}
          onClick={() => navigate('/settings/security')}
        >
          {t('sf.enrolNow')}
        </Button>
        <p className="text-[11.5px] text-subtle leading-snug text-center">
          {t('sf.enrolWhy')}
        </p>
      </div>
    </Shell>
  );
}

/* -----------------------------------------------------------------------------
 * One frame for both, because they are the same moment for the person reading
 * them: stopped at the door, with something to do about it.
 * -------------------------------------------------------------------------- */
function Shell({
  icon, title, body, children, tone = 'brand',
}: {
  icon: ReactNode;
  title: string;
  body: string;
  children: ReactNode;
  tone?: 'brand' | 'warn';
}) {
  const t = useT();

  return (
    <div className="min-h-dvh grid place-items-center bg-bg px-5 py-10">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-7">
          <BrandMark size={38} chip="auto" />
        </div>

        <div className="bg-surface border border-line rounded-card p-7 flex flex-col gap-5">
          <div className="text-center">
            <span
              className={
                tone === 'warn'
                  ? 'inline-grid place-items-center w-12 h-12 rounded-full bg-warn-subtle text-warn'
                  : 'inline-grid place-items-center w-12 h-12 rounded-full bg-brand-subtle text-brand'
              }
            >
              {icon}
            </span>
            <h1 className="text-[18px] font-bold mt-3 tracking-tight">{title}</h1>
            <p className="text-[13px] text-muted mt-1.5 leading-relaxed">{body}</p>
          </div>

          {children}
        </div>

        {/* Always available. Somebody who cannot get past this screen must be
            able to leave it rather than be trapped in their own app. */}
        <div className="flex justify-center mt-4">
          <Button
            variant="ghost"
            size="sm"
            icon={<LogOut size={13} />}
            onClick={() => void supabase.auth.signOut()}
          >
            {t('sf.signOut')}
          </Button>
        </div>
      </div>
    </div>
  );
}
